import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material'
import { IconChecks, IconCopy, IconInfoCircle, IconUsers, IconBuildingStore } from '@tabler/icons-react'
import { QRCodeSVG } from 'qrcode.react'
import { logEvent } from '../../analytics/analytics'
import React, { useState, useEffect, useMemo } from 'react'
import { useAppSelector } from '../../store'
import { selectEater } from '../../store/profileSlice'
import ShareInvite from '../ShareInvite'
import AddIconButton from '../ui/button/AddIconButton'
import { fetchReferralStatus } from '../../services/referralsService'
import { errorLog } from '../../utils/log-helper'
import { buildReferralUrl } from '../../helpers/referral-helper'

interface IReferProps {
  type: 'restaurant' | 'eater'
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const CustomTabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`referral-tabpanel-${index}`}
    aria-labelledby={`referral-tab-${index}`}
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </div>
)

const a11yProps = (index: number): { id: string; 'aria-controls': string } => ({
  id: `referral-tab-${index}`,
  'aria-controls': `referral-tabpanel-${index}`,
})

const Refer: React.FC<IReferProps> = ({ type }) => {
  const eater = useAppSelector(selectEater)
  const earnWays = eater?.earnWays ?? []
  const isBetween960And1430 = useMediaQuery(
    '(min-width:960px) and (max-width:1430px)',
  )
  const [shareInvite, setShareInvite] = useState(false)
  const [loading, setLoading] = useState(true)
  const [openProgress, setOpenProgress] = useState(false)
  
  // Tab state for eaters (Friends = 0, Restaurants = 1)
  const [activeTab, setActiveTab] = useState(0)
  
  // For eaters: two invite objects; for restaurants: single invite object
  interface InviteData {
    referralCode: string
    shareUrl: string
    context: string
  }
  const [friendInvite, setFriendInvite] = useState<InviteData | null>(null)
  const [restaurantInvite, setRestaurantInvite] = useState<InviteData | null>(null)
  const [tableInvite, setTableInvite] = useState<InviteData | null>(null) // For restaurants
  
  // Store counts by context for instant tab switching
  const [countsByContext, setCountsByContext] = useState<Record<string, Record<string, number>>>({})
  
  // Copy state per invite type
  const [copyStates, setCopyStates] = useState<Record<string, { code: boolean; link: boolean }>>({})

  // Fetch referral status on mount
  useEffect(() => {
    const loadReferralCode = async (): Promise<void> => {
      try {
        setLoading(true)
        const status = await fetchReferralStatus()
        
        // Store all counts by context for instant tab switching
        setCountsByContext(status.counts ?? {})
        
        if (type === 'eater') {
          // For eaters: find both friend and restaurant invite codes
          const friendCode = status.myCodes.find((c) => c.context === 'EATER_TO_EATER')
          const restaurantCode = status.myCodes.find((c) => c.context === 'RESTAURANT_SIGNUP')
          
          if (friendCode) {
            setFriendInvite({
              referralCode: friendCode.referralCode,
              shareUrl: buildReferralUrl(friendCode.referralCode),
              context: friendCode.context,
            })
          }
          
          if (restaurantCode) {
            setRestaurantInvite({
              referralCode: restaurantCode.referralCode,
              shareUrl: buildReferralUrl(restaurantCode.referralCode),
              context: restaurantCode.context,
            })
          }
        } else {
          // For restaurants: use EATER_SIGNUP_FROM_TABLE
          const tableCode = status.myCodes.find((c) => c.context === 'EATER_SIGNUP_FROM_TABLE')
          
          if (tableCode) {
            setTableInvite({
              referralCode: tableCode.referralCode,
              shareUrl: buildReferralUrl(tableCode.referralCode),
              context: tableCode.context,
            })
          }
        }
      } catch (error) {
        errorLog('Failed to load referral code', error)
        // Fallback: use empty state (user will see loading/error)
      } finally {
        setLoading(false)
      }
    }
    
    void loadReferralCode()
  }, [type])

  const copyCurrentUrl = async (inviteKey: string, name: 'code' | 'link', invite: InviteData): Promise<void> => {
    const copyString = name === 'code' ? invite.referralCode : invite.shareUrl
    await navigator.clipboard.writeText(copyString)

    // Top of the viral funnel: the referrer shared their invite.
    logEvent('referral_link_shared', {
      context: invite.context,
      method: name === 'code' ? 'copy_code' : 'copy_link',
      screen_name: 'refer_and_earn',
    })

    setCopyStates((prev) => ({
      ...prev,
      [inviteKey]: {
        ...prev[inviteKey],
        [name]: true,
      },
    }))
    
    setTimeout(() => {
      setCopyStates((prev) => ({
        ...prev,
        [inviteKey]: {
          ...prev[inviteKey],
          [name]: false,
        },
      }))
    }, 1000)
  }

  const onCheckEarnings = (): void => {
    setOpenProgress(true)
  }
  
  // Derive active invite and counts based on selected tab
  const activeInvite = useMemo(() => {
    if (type === 'restaurant') {
      return tableInvite
    } else {
      // For eaters: use active tab to determine which invite
      return activeTab === 0 ? friendInvite : restaurantInvite
    }
  }, [type, activeTab, friendInvite, restaurantInvite, tableInvite])
  
  const activeCounts = useMemo(() => {
    if (type === 'restaurant') {
      return countsByContext.EATER_SIGNUP_FROM_TABLE ?? {}
    } else {
      // For eaters: use active tab to determine which counts
      const contextKey = activeTab === 0 ? 'EATER_TO_EATER' : 'RESTAURANT_SIGNUP'
      return countsByContext[contextKey] ?? {}
    }
  }, [type, activeTab, countsByContext])
  
  // Helper to get counts for a specific context
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getCountsForContext = (context: string) => {
    return countsByContext[context] ?? {}
  }
  
  // Helper to calculate totals from counts
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const calculateTotals = (counts: Record<string, number>) => {
    const pending = counts.PENDING ?? 0
    const confirmed = counts.CONFIRMED ?? 0
    const completed = counts.COMPLETED ?? 0
    const invitedTotal = pending + confirmed + completed
    const earnedTotal = completed
    return { invitedTotal, earnedTotal }
  }
  
  // Derived values from active counts (for restaurant flow)
  const { invitedTotal, earnedTotal } = calculateTotals(activeCounts)

  // Determine earnWay key based on active invite context
  const earnKey = useMemo(() => {
    if (type === 'restaurant') {
      return 'table_referral_subscribe'
    } else {
      // For eaters: determine based on active invite context
      if (activeInvite?.context === 'EATER_TO_EATER') {
        return 'refer_friend'
      } else if (activeInvite?.context === 'RESTAURANT_SIGNUP') {
        return 'invite_restaurant'
      } else {
        // Default fallback
        return 'refer_friend'
      }
    }
  }, [type, activeInvite])

  // Find the relevant earnWay and get points
  const earnWay = useMemo(() => {
    return earnKey ? earnWays.find((w) => w.key === earnKey) : undefined
  }, [earnKey, earnWays])

  const youReceivePoints = earnWay?.pointsHint ?? 0

  // Helper function to get copy text based on context
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getCopyText = (context: string, type: 'code' | 'link') => {
    if (type === 'code') {
      if (context === 'EATER_TO_EATER') {
        return 'Share your code — you earn points when they join.'
      } else if (context === 'RESTAURANT_SIGNUP') {
        return 'Share your code — earn points when a restaurant signs up.'
      } else if (context === 'EATER_SIGNUP_FROM_TABLE') {
        return 'Share this QR for table signups — you earn points when they subscribe.'
      }
      return 'Share your code — you earn points when they join.'
    } else {
      if (context === 'EATER_TO_EATER') {
        return 'Share your link or QR — they earn points for onboarding and you earn points when they join.'
      } else if (context === 'RESTAURANT_SIGNUP') {
        return 'Share your link — invite restaurants to join Resactly.'
      } else if (context === 'EATER_SIGNUP_FROM_TABLE') {
        return 'Share this QR for table signups — you earn points when they subscribe.'
      }
      return 'Share your link or QR — they\'ll be credited when they join.'
    }
  }

  // Render right column content (QR + captions + invitation code/link)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const renderInviteRightColumn = (invite: InviteData | null, inviteKey: string) => {
    if (!invite) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Unable to load code. Please try again later.
          </Typography>
        </Box>
      )
    }

    const copyState = copyStates[inviteKey] || { code: false, link: false }

    return (
      <Box>
        {/* QR Code section */}
        <Box display="flex" justifyContent="start" mb={2}>
          <QRCodeSVG value={invite.shareUrl} size={150} color="white" />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Scan to join Resactly with this invite
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block', fontStyle: 'italic' }}>
          Invite type: {
            invite.context === 'EATER_TO_EATER' ? 'Friend invite' :
            invite.context === 'RESTAURANT_SIGNUP' ? 'Restaurant signup' :
            invite.context === 'EATER_SIGNUP_FROM_TABLE' ? 'Table signup' :
            'Invite'
          }
        </Typography>
        
        {/* Invitation Code */}
        <Box mb={{ xs: 2, md: 3 }}>
          <Typography variant="subtitle1" mb="6px">
            Invitation Code
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: 'grey.100',
              p: 1.5,
              borderRadius: '8px',
              cursor: 'pointer',
              border: 1,
              borderColor: 'grey.200',
            }}
            onClick={() => {
              void copyCurrentUrl(inviteKey, 'code', invite)
            }}
          >
            <Typography variant="subtitle1">{invite.referralCode}</Typography>
            {copyState.code ? (
              <IconChecks size={16} stroke={2} />
            ) : (
              <IconCopy size={16} stroke={2} />
            )}
          </Box>
          <Typography variant="subtitle2" color="grey.500" mt={0.5}>
            {getCopyText(invite.context, 'code')}
          </Typography>
        </Box>
        
        {/* Invitation Link */}
        <Box>
          <Typography variant="subtitle1" mb="6px">
            Invitation Link
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: 'grey.100',
              p: 1.5,
              borderRadius: '8px',
              cursor: 'pointer',
              border: 1,
              borderColor: 'grey.200',
            }}
            onClick={() => {
              void copyCurrentUrl(inviteKey, 'link', invite)
            }}
          >
            <Typography variant="subtitle1" sx={{ wordBreak: 'break-all' }}>
              {invite.shareUrl}
            </Typography>
            {copyState.link ? (
              <IconChecks size={16} stroke={2} />
            ) : (
              <IconCopy size={16} stroke={2} />
            )}
          </Box>
          <Typography variant="subtitle2" color="grey.500" mt={0.5}>
            {getCopyText(invite.context, 'link')}
          </Typography>
        </Box>
      </Box>
    )
  }

  // Legacy renderInviteSection for restaurant table flow (kept for backward compatibility)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const renderInviteSection = (invite: InviteData | null, title: string, inviteKey: string) => {
    return (
      <Box mb={4}>
        {renderInviteRightColumn(invite, inviteKey)}
      </Box>
    )
  }

  return (
    <Box>
      <Box
        display="flex"
        alignItems={{ xs: 'self-start', md: 'center' }}
        justifyContent="space-between"
        flexDirection={{ xs: 'column', md: 'row' }}
        mt={{ xs: type === 'eater' ? 3 : 0, md: type === 'eater' ? 6 : 0 }}
        mb={{ xs: 2, md: 3 }}
      >
        <Box>
          {type === 'eater' && (
            <Typography variant="h3">Refer & Earn</Typography>
          )}
          <Typography variant="subtitle1">
            Earn rewards by inviting other restaurants and eaters to join!
          </Typography>
        </Box>

        <AddIconButton
          variant="outlined"
          sx={{
            mt: { xs: 2, md: 0 },
            alignSelf: 'self-start',
          }}
          onClick={() => setShareInvite(true)}
        >
          Share Invite Link
        </AddIconButton>
      </Box>
      
      {/* Tabs for eaters only */}
      {type === 'eater' && (
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 3,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(event: React.SyntheticEvent, newValue: number) => {
              setActiveTab(newValue)
            }}
            aria-label="referral invite type tabs"
          >
            <Tab
              icon={<IconUsers size={20} stroke={2} />}
              iconPosition="start"
              label="Friends"
              {...a11yProps(0)}
              sx={{
                paddingY: '0px',
                minHeight: '56px',
                textTransform: 'capitalize',
              }}
            />
            <Tab
              icon={<IconBuildingStore size={20} stroke={2} />}
              iconPosition="start"
              label="Restaurants"
              {...a11yProps(1)}
              sx={{
                paddingY: '0px',
                minHeight: '56px',
                textTransform: 'capitalize',
              }}
            />
          </Tabs>
        </Box>
      )}
      
      {/* Points pill + QR section in two-column layout */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} my={4}>
          <CircularProgress />
        </Box>
      ) : type === 'eater' ? (
        <>
          {/* Friend invite tab panel */}
          <CustomTabPanel value={activeTab} index={0}>
            <Grid container spacing={{ xs: 2, md: 4 }} mb={3}>
              {/* Left column: Points pill */}
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'warning.200',
                    borderRadius: '8px',
                    py: 2.5,
                    px: 3.5,
                    backgroundColor: 'warning.A100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" mb={1}>
                      You&apos;ll receive
                    </Typography>
                    <Box display="flex" alignItems="baseline">
                      <Typography variant="h2" fontWeight={700} mr={0.5}>
                        {youReceivePoints}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary" pl={0.5}>
                        Points
                      </Typography>
                    </Box>
                  </Box>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 1, borderColor: 'warning.200' }}
                  />

                  <Box textAlign="right">
                    <Typography variant="subtitle1" mb={1}>
                      They will receive
                    </Typography>
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      alignItems="baseline"
                    >
                      <Typography variant="h2" fontWeight={700} mr={0.5}>
                        —
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary" pl={0.5}>
                        Points
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      They&apos;ll receive points when they join.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Right column: QR + invitation code/link */}
              <Grid item xs={12} md={5}>
                {renderInviteRightColumn(friendInvite, 'friend')}
              </Grid>
            </Grid>

            {/* "You've invited..." and Check Earnings button */}
            {(() => {
              const friendCounts = getCountsForContext('EATER_TO_EATER')
              const friendTotals = calculateTotals(friendCounts)
              return (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                    You&apos;ve invited <b>{friendTotals.invitedTotal}</b> people • <b>{friendTotals.earnedTotal}</b> rewards earned so far
                  </Typography>

                  <Button variant="outlined" onClick={onCheckEarnings}>
                    Check Earnings
                  </Button>
                </>
              )
            })()}
          </CustomTabPanel>
          
          {/* Restaurant invite tab panel */}
          <CustomTabPanel value={activeTab} index={1}>
            <Grid container spacing={{ xs: 2, md: 4 }} mb={3}>
              {/* Left column: Points pill */}
              <Grid item xs={12} md={7}>
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'warning.200',
                    borderRadius: '8px',
                    py: 2.5,
                    px: 3.5,
                    backgroundColor: 'warning.A100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" mb={1}>
                      You&apos;ll receive
                    </Typography>
                    <Box display="flex" alignItems="baseline">
                      <Typography variant="h2" fontWeight={700} mr={0.5}>
                        {youReceivePoints}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary" pl={0.5}>
                        Points
                      </Typography>
                    </Box>
                  </Box>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ mx: 1, borderColor: 'warning.200' }}
                  />

                  <Box textAlign="right">
                    <Typography variant="subtitle1" mb={1}>
                      They will receive
                    </Typography>
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      alignItems="baseline"
                    >
                      <Typography variant="h2" fontWeight={700} mr={0.5}>
                        —
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary" pl={0.5}>
                        Points
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      They&apos;ll receive points when they join.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Right column: QR + invitation code/link */}
              <Grid item xs={12} md={5}>
                {renderInviteRightColumn(restaurantInvite, 'restaurant')}
              </Grid>
            </Grid>

            {/* "You've invited..." and Check Earnings button */}
            {(() => {
              const restaurantCounts = getCountsForContext('RESTAURANT_SIGNUP')
              const restaurantTotals = calculateTotals(restaurantCounts)
              return (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                    You&apos;ve invited <b>{restaurantTotals.invitedTotal}</b> people • <b>{restaurantTotals.earnedTotal}</b> rewards earned so far
                  </Typography>

                  <Button variant="outlined" onClick={onCheckEarnings}>
                    Check Earnings
                  </Button>
                </>
              )
            })()}
          </CustomTabPanel>
        </>
      ) : (
        <>
          {/* Restaurant table flow */}
          <Grid container spacing={{ xs: 2, md: 4 }} mb={3}>
            {/* Left column: Points pill */}
            <Grid item xs={12} md={isBetween960And1430 ? 12 : 7}>
              <Box
                sx={{
                  border: 1,
                  borderColor: 'warning.200',
                  borderRadius: '8px',
                  py: 2.5,
                  px: 3.5,
                  backgroundColor: 'warning.A100',
                  mx: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography variant="subtitle1" mb={1}>
                    You&apos;ll receive (when they subscribe)
                  </Typography>
                  <Box display="flex" alignItems="baseline">
                    <Typography variant="h2" fontWeight={700} mr={0.5}>
                      {youReceivePoints}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" pl={0.5}>
                      Points
                    </Typography>
                  </Box>
                </Box>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 1, borderColor: 'warning.200' }}
                />

                <Box textAlign="right">
                  <Typography variant="subtitle1" mb={1}>
                    They will receive
                  </Typography>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="baseline"
                  >
                    <Typography variant="h2" fontWeight={700} mr={0.5}>
                      —
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" pl={0.5}>
                      Credits
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    They&apos;ll be credited when they join.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right column: QR + invitation code/link (only show if not full width) */}
            {!isBetween960And1430 && (
              <Grid item xs={12} md={5}>
                {renderInviteRightColumn(tableInvite, 'table')}
              </Grid>
            )}
          </Grid>

          {/* "You've invited..." and Check Earnings button */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            You&apos;ve invited <b>{invitedTotal}</b> people • <b>{earnedTotal}</b> rewards earned so far
          </Typography>

          <Button variant="outlined" onClick={onCheckEarnings}>
            Check Earnings
          </Button>

          {/* Table invite section for restaurants (full width if isBetween960And1430) */}
          {isBetween960And1430 && (
            <Box mt={3}>
              {renderInviteSection(tableInvite, 'Table signup', 'table')}
            </Box>
          )}
        </>
      )}
      
      <Divider sx={{ my: { xs: 2, md: 3 } }} />
      <Box
        color="secondary.main"
        display="flex"
        alignItems="center"
        flexWrap="wrap"
      >
        <IconInfoCircle size={16} stroke={2} />
        <Typography variant="subtitle1" color="grey.500" pl={1}>
          Invite others and earn points! Get up to an extra
        </Typography>
        <Typography
          variant="subtitle1"
          color="primary.main"
          fontWeight={600}
          px={0.6}
        >
          500 points
        </Typography>
        {/* <Typography variant="subtitle1" color="grey.500"> */}
        {/*  if they join as an influencer. Start sharing and enjoy the rewards */}
        {/* </Typography> */}
        <Typography variant="subtitle1" color="grey.500">
          if they subscribe. Start sharing and enjoy the rewards
        </Typography>
      </Box>
      <ShareInvite
        open={shareInvite}
        handleClose={() => setShareInvite(false)}
        url={activeInvite?.shareUrl ?? ''}
      />
      <Dialog open={openProgress} onClose={() => setOpenProgress(false)} fullWidth maxWidth="xs">
        <DialogTitle>Referral progress</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Track what&apos;s happening after you share your invite.
          </Typography>

          {(() => {
            const currentCounts = activeCounts
            const pending = currentCounts.PENDING ?? 0
            const confirmed = currentCounts.CONFIRMED ?? 0
            const completed = currentCounts.COMPLETED ?? 0
            const totalInvited = pending + confirmed + completed

            return (
              <>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Waiting to sign up</Typography>
                  <Typography fontWeight={600}>{pending}</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Signed up</Typography>
                  <Typography fontWeight={600}>{confirmed}</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Subscribed / earned</Typography>
                  <Typography fontWeight={600}>{completed}</Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box display="flex" justifyContent="space-between">
                  <Typography>Total invited</Typography>
                  <Typography fontWeight={700}>{totalInvited}</Typography>
                </Box>
              </>
            )
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProgress(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Refer
